import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { cosmetics as pageCosmetics } from '../../cosmetics'
import type { Cosmetics } from '../../cosmetics'
import type { NetState } from '../../net/GameClient'
import type { Flow } from '../flow'
import type { AuthStatus, Session } from '../session'
import { errorText } from '../text'
import { useStore } from '../useStore'
import { CustomisePanel } from './CustomisePanel'
import styles from './hub.module.css'
import ov from './overlays.module.css'
import { Icon } from './icons'
import { ItemsRow } from './ItemsRow'
import { PlayWithFriends } from './PlayWithFriends'
import { RunnerStage } from './RunnerStage'
import { isGuest, phase2Slots, topBarValues, useMaybeStore } from './slots'
import { SocialPanel } from './SocialPanel'
import { statusLine } from './statusLine'
import { TopBar } from './TopBar'
import type { TopAction } from './TopBar'

/** Refusals stay up this long (PHASE3 T3.1). */
const ERROR_TOAST_MS = 6000
const INFO_TOAST_MS = 2500

type Sheet = 'style' | 'friends' | null
interface Toast {
  text: string
  error: boolean
  id: number
}

interface Props {
  session: Session
  flow: Flow
  auth: AuthStatus
  net: NetState
  /** Injected by tests; the page's store otherwise. */
  cosmetics?: Cosmetics
}

/**
 * The hub (PHASE3 T3.1, D-048): top bar, customise panel, the runner on its ledge with the items
 * row, the League/Friends panel and the Race block. Below 960 px the side panels become the
 * Style / Friends tabs. Escape closes the topmost card, popover or sheet; Race has focus on
 * arrival. Phase 2's pieces mount through the D-053 slots when they exist.
 */
export function Hub({ session, flow, auth, net, cosmetics = pageCosmetics }: Props) {
  const slots = useMemo(() => phase2Slots(session), [session])
  const profile = useMaybeStore(slots.profile)
  const wallet = useMaybeStore(slots.wallet)
  const charId = useStore(cosmetics.charId)
  const runner = cosmetics.runners.find((r) => r.charId === charId) ?? cosmetics.runners[0]
  const rideId = useStore(cosmetics.equippedRide)
  // A ride tried on in the shop (T3.9a) stands in for the equipped one on the stage only.
  const tryOn = useStore(cosmetics.tryOnRide)
  const shown = tryOn ?? rideId
  const ride = cosmetics.rides.find((r) => r.id === shown) ?? null
  const [item, setItem] = useState<string | null>(null)
  const [friends, setFriends] = useState(flow.error === 'not-found')
  const [sheet, setSheet] = useState<Sheet>(null)
  const [daily, setDaily] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)
  const race = useRef<HTMLButtonElement>(null)
  const name = profile?.displayName ?? session.name

  const say = (text: string, error = false): void =>
    setToast((t) => ({ text, error, id: (t?.id ?? 0) + 1 }))

  useEffect(() => {
    if (!flow.error) return
    // A refused code shows once, in the Play with friends popover beside the code box.
    if (flow.error === 'not-found') setFriends(true)
    else {
      say(errorText(flow.error), true)
      race.current?.focus()
    }
  }, [flow])

  useEffect(() => {
    if (flow.error !== 'not-found') race.current?.focus()
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), toast.error ? ERROR_TOAST_MS : INFO_TOAST_MS)
    return () => clearTimeout(t)
  }, [toast])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (item) setItem(null)
      else if (daily) setDaily(false)
      else if (friends) {
        setFriends(false)
        race.current?.focus()
      } else if (sheet) setSheet(null)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [item, daily, friends, sheet])

  const onAction = (a: TopAction): void => {
    if (a === 'tasks' && slots.DailyBox) setDaily(true)
    else if (a === 'profile' && slots.openProfile) slots.openProfile()
    else if (a === 'settings' && typeof session.openSettings === 'function') session.openSettings()
    else if (a === 'shop' && typeof session.openShop === 'function') session.openShop()
    else say(`${a === 'tasks' ? 'Tasks' : a[0]!.toUpperCase() + a.slice(1)}: coming soon`)
  }

  const DailyBox = slots.DailyBox
  const sheetClass = (s: Sheet): string => (sheet === s ? (styles.sheetOpen ?? '') : '')
  return (
    <div class={styles.hub}>
      <TopBar values={topBarValues(profile, wallet)} name={name} onAction={onAction} />
      <div class={styles.sheetTabs} role="tablist" aria-label="Panels">
        {(['style', 'friends'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={sheet === s}
            class={`${styles.sheetTab} ${sheet === s ? styles.sheetTabOn : ''}`}
            onClick={() => setSheet(sheet === s ? null : s)}
          >
            {s === 'style' ? 'Style' : 'Friends'}
          </button>
        ))}
      </div>
      <div class={`${styles.left} ${sheetClass('style')}`}>
        <CustomisePanel
          runners={cosmetics.runners}
          charId={charId}
          onPick={(id) => cosmetics.setCharId(id)}
          rides={{
            rides: cosmetics.rides,
            equipped: rideId,
            owns: (id) => cosmetics.ownsRide(id),
            onPick: (id) => cosmetics.setRide(id),
          }}
        />
      </div>
      <main class={styles.centre}>
        {runner && (
          <RunnerStage slot={runner} playerName={name} ride={ride} trying={tryOn !== null} />
        )}
        <ItemsRow open={item} onOpen={setItem} />
      </main>
      <div class={`${styles.right} ${sheetClass('friends')}`}>
        <SocialPanel
          myName={name}
          guest={isGuest(profile)}
          runners={cosmetics.runners}
          SignIn={slots.SignIn}
        />
      </div>
      <div class={styles.raceBlock}>
        <button
          ref={race}
          type="button"
          class={styles.raceButton}
          onClick={() => session.playPublic()}
        >
          Race
          <Icon name="go" class={styles.raceIcon} />
        </button>
        <div class={styles.raceLinks}>
          <button
            type="button"
            class={styles.textButton}
            aria-expanded={friends}
            onClick={() => setFriends(!friends)}
          >
            Play with friends
          </button>
          <a class={styles.textButton} href="?practice">
            Practice
          </a>
        </div>
        <p class={styles.statusLine} aria-live="polite">
          {statusLine(auth, net)}
        </p>
        {friends && (
          <PlayWithFriends
            initialCode={session.lastCode}
            error={flow.error}
            onPrivate={() => session.createPrivate()}
            onJoin={(code) => session.joinCode(code)}
            onClose={() => {
              setFriends(false)
              race.current?.focus()
            }}
            PartyPanel={slots.PartyPanel}
          />
        )}
      </div>
      {daily && DailyBox && (
        <div class={ov.modal} role="dialog" aria-label="Tasks">
          <div class={`${ov.card} ${ov.modalCard}`}>
            <button
              type="button"
              class={ov.closeButton}
              aria-label="Close"
              onClick={() => setDaily(false)}
            >
              <Icon name="close" />
            </button>
            <DailyBox />
          </div>
        </div>
      )}
      {toast && (
        <div
          key={toast.id}
          class={`${ov.toast} ${toast.error ? ov.toastError : ''}`}
          role={toast.error ? 'alert' : 'status'}
        >
          {toast.text}
        </div>
      )}
    </div>
  )
}
